/**
 * GA4 Enhanced Tracking Module
 * Adds UTM parameter capture and complete event coverage
 */
// Import via '@/lib/tracking' — direct imports are deprecated (see lib/tracking/index.ts).

import { readStorage, removeStorage, writeStorage } from '@/lib/safe-storage';

declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'js',
      action: string,
      params?: Record<string, string | number | boolean | undefined>
    ) => void;
    dataLayer?: unknown[];
  }
}

// ============================================
// UTM PARAMETER TRACKING
// ============================================

export interface UTMParams {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
}

const UTM_PARAMS_KEY = 'utm_params_v1';
const UTM_CAPTURE_TIME_KEY = 'utm_capture_time_v1';

/**
 * Parse UTM parameters from current URL
 */
export function getUTMParams(): UTMParams {
  if (typeof window === 'undefined') {
    return { source: null, medium: null, campaign: null, content: null, term: null };
  }
  
  const url = new URL(window.location.href);
  return {
    source: url.searchParams.get('utm_source'),
    medium: url.searchParams.get('utm_medium'),
    campaign: url.searchParams.get('utm_campaign'),
    content: url.searchParams.get('utm_content'),
    term: url.searchParams.get('utm_term'),
  };
}

/**
 * Get UTM params from localStorage (for session persistence)
 */
export function getStoredUTMParams(): UTMParams | null {
  const stored = readStorage(UTM_PARAMS_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Store UTM params in localStorage when they exist in URL
 * Call this on app initialization
 */
export function captureUTMParams(): UTMParams {
  const params = getUTMParams();
  
  // Only store if there are actual UTM params
  if (params.source || params.medium || params.campaign) {
    writeStorage(UTM_PARAMS_KEY, JSON.stringify(params));
    writeStorage(UTM_CAPTURE_TIME_KEY, Date.now().toString());
  }

  return params;
}

/**
 * Get UTM params with fallback to stored values
 * Returns active params first, then stored, then null
 */
export function getEffectiveUTMParams(): UTMParams | null {
  const current = getUTMParams();
  
  // If current URL has UTMs, use those (new campaign)
  if (current.source || current.medium || current.campaign) {
    return current;
  }
  
  // Otherwise use stored UTMs from original entry
  return getStoredUTMParams();
}

/**
 * Clear stored UTM params (e.g., after conversion)
 */
export function clearStoredUTMParams(): void {
  removeStorage(UTM_PARAMS_KEY);
  removeStorage(UTM_CAPTURE_TIME_KEY);
}

// ============================================
// REFERRER TRACKING
// ============================================

export interface ReferrerData {
  referrer: string | null;
  referrer_domain: string | null;
  isDirect: boolean;
  isOrganic: boolean;
  searchEngine: string | null;
}

/**
 * Get referrer information
 */
export function getReferrerData(): ReferrerData {
  if (typeof window === 'undefined') {
    return { 
      referrer: null, 
      referrer_domain: null, 
      isDirect: true, 
      isOrganic: false, 
      searchEngine: null 
    };
  }
  
  const referrer = document.referrer;
  const currentDomain = window.location.hostname;
  
  if (!referrer) {
    return { 
      referrer: null, 
      referrer_domain: null, 
      isDirect: true, 
      isOrganic: false, 
      searchEngine: null 
    };
  }
  
  const referrerUrl = new URL(referrer);
  const referrerDomain = referrerUrl.hostname;
  
  // Don't track self-referrals
  if (referrerDomain === currentDomain) {
    return { 
      referrer: 'internal', 
      referrer_domain: currentDomain, 
      isDirect: false, 
      isOrganic: false, 
      searchEngine: null 
    };
  }
  
  const searchEngines: Record<string, string> = {
    'google': 'Google',
    'bing': 'Bing',
    'yahoo': 'Yahoo',
    'duckduckgo': 'DuckDuckGo',
    'baidu': 'Baidu',
  };
  
  const searchEngine = Object.keys(searchEngines).find(se => referrerDomain.includes(se));
  
  return {
    referrer: referrerDomain,
    referrer_domain: referrerDomain,
    isDirect: false,
    isOrganic: !!searchEngine,
    searchEngine: searchEngine ? searchEngines[searchEngine] : null,
  };
}

// ============================================
// ENHANCED GA4 EVENT TRACKING
// ============================================

function safeGA4Event(
  eventName: string,
  properties?: Record<string, string | number | boolean | undefined>
) {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      const cleanProps = Object.fromEntries(
        Object.entries(properties || {}).filter(([, v]) => v !== undefined)
      ) as Record<string, string | number | boolean>;
      
      window.gtag('event', eventName, cleanProps);
    }
  } catch {
    // GA4 should never block core user flows
  }
}

/**
 * Get enhanced event properties with UTM and referrer data
 */
function getEnhancedEventProperties(): Record<string, string | boolean> {
  const utm = getEffectiveUTMParams();
  const referrer = getReferrerData();
  
  return {
    utm_source: utm?.source || '',
    utm_medium: utm?.medium || '',
    utm_campaign: utm?.campaign || '',
    utm_content: utm?.content || '',
    utm_term: utm?.term || '',
    referrer_domain: referrer.referrer_domain || '',
    is_organic: referrer.isOrganic,
  };
}

// ============================================
// PAGE VIEW TRACKING
// ============================================

/**
 * Track enhanced page view with UTM and referrer data
 */
function trackEnhancedPageView(pagePath?: string) {
  const utm = getEffectiveUTMParams();
  const referrer = getReferrerData();
  
  safeGA4Event('page_view', {
    page_location: pagePath || (typeof window !== 'undefined' ? window.location.href : ''),
    page_title: typeof document !== 'undefined' ? document.title : '',
    utm_source: utm?.source || '',
    utm_medium: utm?.medium || '',
    utm_campaign: utm?.campaign || '',
    utm_content: utm?.content || '',
    utm_term: utm?.term || '',
    referrer_domain: referrer.referrer_domain || '',
    is_organic: referrer.isOrganic,
    has_utm_params: !!(utm?.source || utm?.medium || utm?.campaign),
  });
}

// ============================================
// ENGAGEMENT EVENTS
// ============================================

function trackSocialShare(props: {
  platform: 'twitter' | 'facebook' | 'linkedin' | 'reddit' | 'email' | 'clipboard';
  content_type: 'sound' | 'page' | 'collection';
  content_id?: string;
  sound_category?: string;
}) {
  const enhanced = getEnhancedEventProperties();
  
  safeGA4Event('social_share', {
    ...enhanced,
    platform: props.platform,
    content_type: props.content_type,
    content_id: props.content_id || '',
    sound_category: props.sound_category || '',
    event_category: 'engagement',
  });
}

// ============================================
// SESSION INITIALIZATION
// ============================================

/**
 * Initialize GA4 tracking on app load
 * Call this in layout.tsx or _app.tsx
 */
export function initializeGA4Tracking() {
  // Capture UTM params on initial load
  captureUTMParams();
  
  // Track initial page view with attribution
  trackEnhancedPageView();
  
  // Set up navigation tracking for single-page apps
  if (typeof window !== 'undefined') {
    // Track on URL changes (Next.js navigation)
    const originalPushState = history.pushState;
    history.pushState = function(...args) {
      originalPushState.apply(this, args);
      trackEnhancedPageView();
    };
    
    const originalReplaceState = history.replaceState;
    history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      trackEnhancedPageView();
    };
    
    // Track on popstate (back/forward buttons)
    window.addEventListener('popstate', () => {
      trackEnhancedPageView();
    });
  }
}
